/* Cart drawer: one-tap upgrade to the next size up, for one set in the cart.
   Swaps the line in a single /cart/update.js call, then lets the theme's own ajax cart
   re-render (via its storage-sync path) and re-injects the offer after every render. */
(function () {
  'use strict';

  var FRAME_BORDER_IN = 1;   // Premium Frame adds this on every side of each piece
  var PIECE_GAP_IN = 3;      // spacing between pieces in a set
  var UNDO_MS = 8000;

  var productCache = {};     // handle -> Promise<product json>
  var lastState = null;
  var undos = {};            // upgraded variant id -> { fromId, toId, quantity, fromSize, toSize, value, handle, until }
  var busy = false;
  var viewed = {};
  // Lines we upgraded this visit (keyed by the variant they were upgraded TO) get no further
  // offer, so a set is never pushed up size after size. Removing and re-adding a set, or
  // undoing, makes it a fresh decision again.
  var HANDLED_KEY = 'percept-cart-upgraded-variants';
  var handled = {};
  try { handled = JSON.parse(window.sessionStorage.getItem(HANDLED_KEY) || '{}') || {}; } catch (e) { handled = {}; }
  function saveHandled() {
    try { window.sessionStorage.setItem(HANDLED_KEY, JSON.stringify(handled)); } catch (e) { /* storage unavailable */ }
  }
  function markHandled(variantId) { handled[variantId] = true; saveHandled(); }
  function clearHandled(variantId) { delete handled[variantId]; saveHandled(); }

  function drawer() { return document.querySelector('.drawer[data-section-type="cart-drawer"]'); }

  function units() {
    try {
      var stored = window.localStorage.getItem('percept-size-guide-units');
      if (stored === 'in' || stored === 'cm') return stored;
    } catch (e) { /* storage unavailable */ }
    var d = drawer();
    return d && d.getAttribute('data-default-units') === 'in' ? 'in' : 'cm';
  }

  function parseSize(value) {
    var m = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i.exec(value || '');
    return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null;
  }

  function formatLength(inches) {
    if (units() === 'cm') return Math.round(inches * 2.54) + ' cm';
    var total = Math.round(inches);
    var ft = Math.floor(total / 12);
    var rest = total % 12;
    if (!ft) return rest + ' in';
    return rest ? ft + ' ft ' + rest + ' in' : ft + ' ft';
  }

  function formatMoney(cents) {
    var format = window.theme && theme.moneyFormat ? theme.moneyFormat : '${{amount}}';
    var key = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    var sep = key.indexOf('comma_separator') !== -1 ? '.' : ',';
    var value = String(Math.round(Number(cents) / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    var holder = document.createElement('div');
    holder.innerHTML = format.replace(/\{\{\s*\w+\s*\}\}/, value).replace('$ ', '$');
    return holder.textContent;
  }

  function pieceCount(productId) {
    var el = document.querySelector('[data-cart-drawer-bundle-composites]');
    if (!el) return 1;
    try { return (JSON.parse(el.textContent)[String(productId)] || []).length || 1; } catch (e) { return 1; }
  }

  function track(name, params) {
    try {
      params.event = name;
      (window.dataLayer = window.dataLayer || []).push(params);
    } catch (e) { /* analytics must never break the cart */ }
  }

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('product ' + r.status); return r.json(); })
        .catch(function (err) { delete productCache[handle]; throw err; });
    }
    return productCache[handle];
  }

  function offerFor(item, product) {
    return nextSizeOffer(item, product) || frameOffer(item, product);
  }

  function optionIndex(product, test) {
    var names = (product.options || []).map(function (o) { return String(typeof o === 'string' ? o : o.name); });
    for (var i = 0; i < names.length; i++) if (test(names[i].toLowerCase(), product.options[i])) return i;
    return -1;
  }

  function variantWith(product, base, index, value) {
    return (product.variants || []).filter(function (v) {
      return v.available && v.options.every(function (opt, idx) {
        return idx === index ? opt === value : opt === base.options[idx];
      });
    })[0] || null;
  }

  // The next size up with the same other options, or null.
  function nextSizeOffer(item, product) {
    var sizeIndex = optionIndex(product, function (n) { return n === 'size'; });
    if (sizeIndex === -1) return null;
    var current = (product.variants || []).filter(function (v) { return v.id === item.variant_id; })[0];
    if (!current) return null;
    var sizeValues = (product.options[sizeIndex].values || []);
    var at = sizeValues.indexOf(current.options[sizeIndex]);
    if (at === -1 || at === sizeValues.length - 1) return null;
    var nextSize = sizeValues[at + 1];
    var target = variantWith(product, current, sizeIndex, nextSize);
    if (!target) return null;
    var from = parseSize(current.options[sizeIndex]);
    var to = parseSize(nextSize);
    if (!from || !to) return null;
    var framed = current.options.some(function (o) { return /frame/i.test(o); });
    var n = pieceCount(item.product_id);
    var pieceW = to.w + (framed ? 2 * FRAME_BORDER_IN : 0);
    var setW = n * pieceW + (n - 1) * PIECE_GAP_IN;
    var ratio = Math.round((to.w * to.h) / (from.w * from.h) * 10) / 10;
    var toSize = to.w + '×' + to.h;
    return {
      target: target,
      fromSize: from.w + '×' + from.h,
      toSize: toSize,
      eyebrow: 'Size up to ' + toSize,
      detail: ratio >= 1.2 ? 'for ' + ratio + '× the surface area' : formatLength(setW) + ' wide',
      doneText: 'Upgraded to ' + toSize,
      diff: lineDiff(item, current, target)
    };
  }

  // 40×60 Gallery Wrapped -> Premium Frame, for US shoppers only (40×60 frames ship to the US only).
  function frameOffer(item, product) {
    var d = drawer();
    if (!d || d.getAttribute('data-country') !== 'US') return null;
    var current = (product.variants || []).filter(function (v) { return v.id === item.variant_id; })[0];
    if (!current) return null;
    var sizeIndex = optionIndex(product, function (n) { return n === 'size'; });
    var finishIndex = optionIndex(product, function (n, o) {
      return (o && o.values ? o.values : []).indexOf('Premium Frame') !== -1;
    });
    if (sizeIndex === -1 || finishIndex === -1) return null;
    var size = parseSize(current.options[sizeIndex]);
    if (!size || size.w !== 40 || size.h !== 60 || current.options[finishIndex] !== 'Gallery Wrapped') return null;
    var target = variantWith(product, current, finishIndex, 'Premium Frame');
    if (!target) return null;
    return {
      target: target,
      fromSize: '40×60 Gallery Wrapped',
      toSize: '40×60 Premium Frame',
      eyebrow: 'Add Premium Frame',
      detail: 'the way we\u2019d hang it',
      doneText: 'Upgraded to Premium Frame',
      diff: lineDiff(item, current, target)
    };
  }

  // Scale catalogue prices by what this line actually costs in the cart, so the shown
  // difference follows line discounts and the shopper's currency.
  function lineDiff(item, current, target) {
    var scale = current.price > 0 ? item.final_price / current.price : 1;
    return Math.round((target.price - current.price) * scale) * item.quantity;
  }

  function itemElements() {
    var list = document.querySelectorAll('.cart-drawer [data-cart-item]:not(.ajax-cart__template):not([data-cart-note-container])');
    return Array.prototype.slice.call(list);
  }

  function clearInjected() {
    Array.prototype.forEach.call(document.querySelectorAll('.cart-drawer [data-cart-upgrade]'), function (el) { el.remove(); });
  }

  function renderUndo(itemEl, info) {
    var box = document.createElement('div');
    box.className = 'cart-upgrade cart-upgrade--done';
    box.setAttribute('data-cart-upgrade', '');
    var text = document.createElement('p');
    text.className = 'cart-upgrade__done-text';
    text.textContent = info.doneText;
    var undo = document.createElement('button');
    undo.type = 'button';
    undo.className = 'cart-upgrade__undo';
    undo.textContent = 'Undo';
    undo.addEventListener('click', function () { doUndo(undo, info); });
    box.appendChild(text);
    box.appendChild(undo);
    itemEl.querySelector('.cart-drawer__item-content').appendChild(box);
  }

  function renderOffer(itemEl, item, offer) {
    var box = document.createElement('div');
    box.className = 'cart-upgrade';
    box.setAttribute('data-cart-upgrade', '');

    // Two rows: what you get on the left, what it costs and the action on the right.
    var eyebrow = document.createElement('p');
    eyebrow.className = 'cart-upgrade__eyebrow';
    eyebrow.textContent = offer.eyebrow;
    var cost = document.createElement('p');
    cost.className = 'cart-upgrade__cost';
    cost.textContent = '+' + formatMoney(offer.diff);
    var detail = document.createElement('p');
    detail.className = 'cart-upgrade__detail';
    detail.textContent = offer.detail;

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'cart-upgrade__button btn-link';
    button.setAttribute('aria-label', offer.eyebrow + ', ' + formatMoney(offer.diff) + ' more');
    button.textContent = 'Upgrade';
    button.addEventListener('click', function () { doUpgrade(button, item, offer); });

    // Two blocks, centred against each other: what you get | what it costs + the action.
    var copy = document.createElement('div');
    copy.className = 'cart-upgrade__copy';
    copy.appendChild(eyebrow);
    copy.appendChild(detail);
    var action = document.createElement('div');
    action.className = 'cart-upgrade__action';
    action.appendChild(cost);
    action.appendChild(button);
    box.appendChild(copy);
    box.appendChild(action);
    itemEl.querySelector('.cart-drawer__item-content').appendChild(box);

    var viewKey = item.variant_id + '>' + offer.target.id;
    if (!viewed[viewKey]) {
      viewed[viewKey] = true;
      track('cart_upgrade_view', { product_handle: item.handle, from_size: offer.fromSize, to_size: offer.toSize, upgrade_value: offer.diff / 100 });
    }
  }

  function render(state) {
    if (!state || !state.items) return;
    lastState = state;
    clearInjected();
    var els = itemElements();
    var now = Date.now();
    Object.keys(undos).forEach(function (id) { if (undos[id].until <= now) delete undos[id]; });
    // One offer at a time: the first set (in cart order) that has a next size and hasn't been
    // upgraded yet. Once it is upgraded, the offer moves on to the next set.
    var candidates = [];
    state.items.forEach(function (item, i) {
      var el = els[i];
      if (!el || !item.handle) return;
      if (undos[item.variant_id]) { renderUndo(el, undos[item.variant_id]); return; }
      if (handled[item.variant_id]) return;
      if (!plainLine(item)) return;
      candidates.push({ item: item, el: el });
    });
    (function next(i) {
      if (i >= candidates.length) return;
      var c = candidates[i];
      getProduct(c.item.handle).then(function (product) {
        if (lastState !== state) return; // a newer render took over
        var offer = offerFor(c.item, product);
        var clash = offer && lineFor(state, offer.target.id, c.item.key);
        if (clash && !plainLine(clash)) offer = null;
        if (offer) renderOffer(c.el, c.item, offer);
        else next(i + 1);
      }).catch(function () { next(i + 1); });
    })(0);
  }

  // Hand the new cart to the theme's ajax cart through its cross-tab sync path.
  function applyState(state) {
    try {
      window.localStorage.shopify_cart_state = JSON.stringify(state);
      window.dispatchEvent(new StorageEvent('storage', { key: 'shopify_cart_state' }));
    } catch (e) {
      window.location.reload();
    }
  }

  // A line we can safely merge into or take units from: no custom properties, no subscription.
  function plainLine(line) {
    var props = line.properties || {};
    return !line.selling_plan_allocation &&
      !Object.keys(props).some(function (k) { return k.charAt(0) !== '_' && props[k]; });
  }

  function lineFor(state, variantId, exceptKey) {
    return (state.items || []).filter(function (i) {
      return i.variant_id === variantId && i.key !== exceptKey;
    })[0] || null;
  }

  // Move `quantity` units from one line to a variant in a single /cart/update.js call.
  // If the cart already has a line of the target variant, its quantity is raised instead of
  // being overwritten (setting a variant's quantity would silently drop the existing units).
  function moveUnits(state, fromLine, quantity, toVariantId) {
    var updates = {};
    updates[fromLine.key] = Math.max(0, fromLine.quantity - quantity);
    var existing = lineFor(state, toVariantId, fromLine.key);
    if (existing) updates[existing.key] = existing.quantity + quantity;
    else updates[toVariantId] = quantity;
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); });
  }

  function fail(button, label) {
    busy = false;
    button.disabled = false;
    button.textContent = label;
    var box = button.closest('[data-cart-upgrade]');
    if (box && !box.querySelector('.cart-upgrade__error')) {
      var err = document.createElement('p');
      err.className = 'cart-upgrade__error';
      err.textContent = 'Couldn’t update the cart. Please try again.';
      box.appendChild(err);
    }
  }

  function doUpgrade(button, item, offer) {
    if (busy) return;
    busy = true;
    var label = button.textContent;
    button.disabled = true;
    button.classList.add('is-busy');
    track('cart_upgrade_accept', { product_handle: item.handle, from_size: offer.fromSize, to_size: offer.toSize, upgrade_value: offer.diff / 100 });
    moveUnits(lastState, item, item.quantity, offer.target.id).then(function (state) {
      busy = false;
      markHandled(offer.target.id);
      undos[offer.target.id] = { productId: item.product_id, fromId: item.variant_id, toId: offer.target.id, quantity: item.quantity, fromSize: offer.fromSize, toSize: offer.toSize, doneText: offer.doneText, value: offer.diff / 100, handle: item.handle, until: Date.now() + UNDO_MS };
      applyState(state);
      setTimeout(function () { if (lastState) render(lastState); }, UNDO_MS + 50);
    }).catch(function () { fail(button, label); });
  }

  function doUndo(button, info) {
    if (busy || !lastState) return;
    var line = lineFor(lastState, info.toId);
    if (!line || line.quantity < info.quantity) return;
    busy = true;
    button.disabled = true;
    track('cart_upgrade_undo', { product_handle: info.handle, from_size: info.fromSize, to_size: info.toSize, upgrade_value: info.value });
    moveUnits(lastState, line, info.quantity, info.fromId).then(function (state) {
      busy = false;
      delete undos[info.toId];
      clearHandled(info.toId);
      applyState(state);
    }).catch(function () { fail(button, 'Undo'); });
  }

  function init() {
    if (!drawer()) return;
    if (window.jQuery) {
      window.jQuery(document).on('cart_update_done.perceptCartUpgrade', function (event, instance, state) {
        render(state);
      });
    }
    fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(render).catch(function () {});
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
